// src/services/submissionService.js

import axios from "axios";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
  deleteDoc,
  collection,
  getDocs,
  query,
  onSnapshot,
  addDoc,
  where,
  orderBy,
} from "firebase/firestore";
import { db } from "../firebase";
import { generateRandomNumber, getAuthToken } from "../utils/utils"; // Import utility functions

const SELL_ARTWORK_URL = process.env.REACT_APP_SELL_ARTWORK;

// API call to generate submission ID
export const generateSubmissionId = async (submissionData) => {
  console.log("submissionData: ", submissionData);

  try {
    const token = getAuthToken(); // Get the token from localStorage
    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    };

    // Generate a title with a random number if not provided
    const generatedTitle = `<No_Title>_${generateRandomNumber()}`;

    console.log("generatedTitle: ", generatedTitle);

    // Send a POST request to generate the artwork ID with the required body
    const response = await axios.post(
      SELL_ARTWORK_URL,
      {
        CampaignID: "",
        AllowPurchase: true,
        Art: {
          Id: null,
          ArtworkId: null,
          Title: generatedTitle,
          Artist: "",
          ArtistFirstName: "",
          ArtistLastName: "",
          Year: "",
          Description: "",
          Condition: "",
          Dimension: "",
          Medium: "",
          Resolution: "",
          MarkAsMature: false,
          AllowWallpaper: false,
          ArtistBiography: "",
          ArtistWebURL: "",
          Tags: "",
          uploadedMediaType: "",
          expectedMediaType: "",
          swayerConvertSuccess: false,
          systemGeneratedTitle: true,
          Location: {
            PlaceholderUrl: "",
            OriginalUrl: "",
            CdnUrl: "",
          },
          Theme: {
            BorderColor: "",
          },
          EventId: null,
          Hash: "",
          IsFeatured: false,
          AllowPurchase: true,
          IsKbeanSelect: false,
          categories: [],
        },
        Sale: {
          Id: null,
          ArtworkId: null,
          SellerId: "",
          IsPrimarySale: true,
          SellAsOne: false,
          SaleComplete: false,
          IsDraft: true,
          IsPause: false,
          Shares: {
            PricePerShare: submissionData.pricePerShare || 0,
            InitialValue: submissionData.initialValue || 0,
            TotalShares: submissionData.totalShares || 1,
          },
        },
      },
      { headers }
    );

    if (response.data && response.data.art && response.data.art.artworkId) {
      return response.data.art.artworkId;
    } else {
      throw new Error("Failed to retrieve submission ID");
    }
  } catch (error) {
    console.error("Error generating submission ID:", error);
    throw error;
  }
};

// Fetch submission data based on submissionId to check for available shares/editions
export const searchSubmissionById = async (submissionId) => {
  try {
    // Retrieve the auth token from localStorage
    const authResponseData = JSON.parse(
      localStorage.getItem("authResponseData")
    );
    const token = authResponseData?.token;

    // Define the request body
    const payload = {
      Sort: '{"Sale.SaleStart":-1}',
      Page: 0,
      Size: 10,
      GroupBy: "artwork",
      FilterMatureArtworks: false,
      FilterKbeanSelectOnly: false,
      Criteria: [
        {
          type: "artworkId",
          value: submissionId, // Pass the submissionId dynamically
        },
      ],
    };

    // Define the request headers
    const headers = {
      "Content-Type": "application/json",
      ApiKey: process.env.REACT_APP_SEARCH_API_KEY, // Pass the API key from .env
    };

    // If token is available, add Authorization header
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else {
      console.warn(
        "User is not authenticated. Proceeding with an unauthenticated request."
      );
    }

    // Make the POST request
    const response = await axios.post(
      process.env.REACT_APP_SEARCH_ARTWORKS_URL,
      payload,
      { headers }
    );

    // Log the response to the console
    // console.log("Artwork search response:", response.data);

    return response.data; // Optionally return the data
  } catch (error) {
    console.error("Error searching for artwork:", error);
    throw error; // Optionally throw the error if needed
  }
};

// Fetch submission details based on submissionId
export const fetchSubmissionDetails = async (submissionId) => {
  try {
    // Create a reference to the submission document in the main submissions collection
    const submissionDocRef = doc(db, "submissions", submissionId);

    // Fetch the document snapshot
    const submissionDocSnap = await getDoc(submissionDocRef);

    if (submissionDocSnap.exists()) {
      // Extract and return the submission data
      return { id: submissionDocSnap.id, ...submissionDocSnap.data() };
    } else {
      console.error("No such document!");
      throw new Error("Submission not found.");
    }
  } catch (error) {
    console.error("Error fetching submission details:", error);
    throw error;
  }
};

/**
 * Fetches submissions from Firestore.
 * If challengeId is provided, it filters submissions by that challengeId.
 * Otherwise, it fetches all published submissions.
 *
 * @param {string} [challengeId] - Optional challenge ID to filter submissions.
 * @returns {Promise<Array>} - A promise that resolves to an array of submissions.
 */
export const fetchChallengeSubmissions = async (challengeId) => {
  try {
    const submissionsCollectionRef = collection(db, "submissions");

    // Base query: status is "published"
    let submissionsQuery = query(
      submissionsCollectionRef,
      where("status", "==", "published")
    );

    // If challengeId is provided, add it to the query
    if (challengeId) {
      submissionsQuery = query(
        submissionsCollectionRef,
        where("status", "==", "published"),
        where("challengeId", "==", challengeId)
      );
    }

    // Always order by createdAt descending
    submissionsQuery = query(submissionsQuery, orderBy("createdAt", "desc"));

    const submissionsSnapshot = await getDocs(submissionsQuery);
    const submissions = submissionsSnapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return submissions;
  } catch (error) {
    console.error("Error fetching submissions:", error);
    throw error;
  }
};

// Function to handle likes and dislikes on submissions
export const handleVote = async (submissionId, userId, voteType) => {
  try {
    // Reference to the votes document within the "votes" subcollection of the "submissions" collection
    const voteRef = doc(db, `submissions/${submissionId}/votes`, userId);
    const voteDoc = await getDoc(voteRef);

    if (voteDoc.exists()) {
      const currentVote = voteDoc.data();

      // If the current vote matches the provided voteType, remove the vote (unlike or undislike)
      if (currentVote[voteType]) {
        await deleteDoc(voteRef);
        return { success: true, message: `${voteType} removed` };
      }

      // If the user wants to switch their vote, update the vote
      const updatedVote = {
        like: voteType === "like",
        dislike: voteType === "dislike",
      };

      await setDoc(voteRef, updatedVote);
      return { success: true, message: `${voteType} updated` };
    } else {
      // If no vote exists, create a new vote document
      const newVote = {
        like: voteType === "like",
        dislike: voteType === "dislike",
      };

      await setDoc(voteRef, newVote);
      return { success: true, message: `${voteType} added` };
    }
  } catch (error) {
    console.error("Error handling vote:", error);
    return { success: false, message: "Error handling vote" };
  }
};

// Function to count likes and dislikes for a submission
export const countVotes = async (submissionId) => {
  try {
    // Reference to the "votes" subcollection within the "submissions" collection
    const votesCollectionRef = collection(
      db,
      `submissions/${submissionId}/votes`
    );
    const voteSnapshot = await getDocs(votesCollectionRef);

    let likeCount = 0;
    let dislikeCount = 0;

    // Iterate through the votes and count likes and dislikes
    voteSnapshot.forEach((doc) => {
      const data = doc.data();
      if (data.like) likeCount++;
      if (data.dislike) dislikeCount++;
    });

    return { likeCount, dislikeCount };
  } catch (error) {
    console.error("Error counting votes:", error);
    return { likeCount: 0, dislikeCount: 0 };
  }
};

// Function to check the current user's vote on a specific submission
export const checkUserVote = async (submissionId, userId) => {
  try {
    // Reference to the specific user's vote in the "votes" subcollection of a "submission" document
    const voteRef = doc(db, `submissions/${submissionId}/votes`, userId);
    const voteDoc = await getDoc(voteRef);

    if (voteDoc.exists()) {
      const voteData = voteDoc.data();
      if (voteData.like) return "like";
      if (voteData.dislike) return "dislike";
    }

    return null; // User hasn't voted yet
  } catch (error) {
    console.error("Error checking user vote:", error);
    return null;
  }
};

// Fetch comments for a specific submission
export const fetchComments = (
  submissionId,
  setComments,
  setLoadingComments
) => {
  try {
    // Reference to the comments subcollection within a specific submission document
    const commentsRef = collection(db, "submissions", submissionId, "comments");
    const q = query(commentsRef);

    // Listen for real-time updates
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const fetchedComments = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setComments(fetchedComments);
      setLoadingComments(false);
    });

    return unsubscribe; // Return the unsubscribe function to stop listening if needed
  } catch (error) {
    console.error("Error fetching comments:", error);
    setLoadingComments(false);
  }
};

// Function to add a new comment to a specific submission
export const addComment = async (submissionId, newComment, user) => {
  try {
    // Reference to the comments subcollection within a specific submission document
    const commentsRef = collection(db, "submissions", submissionId, "comments");

    // Add a new comment to the Firestore comments subcollection
    await addDoc(commentsRef, {
      text: newComment,
      createdAt: serverTimestamp(), // Firestore server timestamp for accurate time
      createdBy: user.displayName || "Anonymous",
      userId: user.uid,
    });

    console.log("Comment added successfully!");
  } catch (error) {
    console.error("Error adding comment:", error);
    throw error;
  }
};

// Function to fetch all holdings (purchases and contributions from Kbean Azure backend)
export async function fetchHoldings() {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    // 3. Build the request body.
    const requestBody = {
      Sort: "{Created: -1}",
      Page: 0,
      Size: 100,
      GroupBy: "artwork",
      FilterMatureArtworks: false,
      FilterKbeanSelectOnly: false,
      Criteria: [],
    };

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_GET_MY_HOLDINGS, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      throw new Error("Failed to fetch holdings");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetchHoldings:", error);
    throw error;
  }
}

// Get editions configuration
export async function getEditionConfiguration() {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_GET_EDITION_CONFIGURATION, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch holdings");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetchHoldings:", error);
    throw error;
  }
}
// Get Events
export async function getEvents() {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_GET_EVENTS, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
    });

    if (!response.ok) {
      throw new Error("Failed to fetch events");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetch events:", error);
    throw error;
  }
}
// Get Spotlight (Popular Artists)
export async function getPopularArtists() {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    const SearchTO = {
      Sort: JSON.stringify({ "Sale.SaleStart": -1 }),
      Page: 0,
      Size: 100,
      GroupBy: 'artwork',
      FilterMatureArtworks: false,
      FilterKbeanSelectOnly: false,
      Criteria: [],
      ArtworkIds: []
    }    

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_POPULAR_ARTISTS , {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body:JSON.stringify(SearchTO)
    });
    
    if (!response.ok) {
      throw new Error("Failed to fetch artists");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetch artists:", error);
    throw error;
  }
}
// Get Spotlight (Popular Artists)
export async function getMyActivity() {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    const SearchTO = {
      Sort: '{Created: -1}',
      Page: 0,
      Size: 100,
      GroupBy: 'artwork',
      Criteria: [],
    }    

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_GET_MY_ACTIVITY , {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body:JSON.stringify(SearchTO)
    });
    
    if (!response.ok) {
      throw new Error("Failed to fetch artists");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetch artists:", error);
    throw error;
  }
}

// Get Artworks By EventID
export async function getArtworksByEventID(id) {
  try {
    // 1. Retrieve authResponseData from localStorage.
    const storedData = localStorage.getItem("authResponseData");
    if (!storedData) {
      throw new Error("No authResponseData found in localStorage.");
    }

    // 2. Parse the stored data and extract the token property.
    const parsedData = JSON.parse(storedData);
    const bearerToken = parsedData.token;
    if (!bearerToken) {
      throw new Error("No token found in authResponseData.");
    }

    const SearchTO = {
      Sort: JSON.stringify({ "Sale.SaleStart": -1 }),
      Page: 0,
      Size: 100,
      GroupBy: 'artwork',
      Criteria: [{type:"event",value:id}],
    }    

    // 4. Send the POST request with the bearer token in the headers.
    const response = await fetch(process.env.REACT_APP_GET_EVENTS_BY_ID , {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${bearerToken}`,
      },
      body:JSON.stringify(SearchTO)
    });
    
    if (!response.ok) {
      throw new Error("Failed to fetch artists");
    }

    // 5. Parse and return the JSON data.
    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error in fetch artists:", error);
    throw error;
  }
}
/**
 * Fetches all submissions from Firestore, ordered by creation date descending.
 * Each submission includes its `paymentData` array.
 *
 * @returns {Promise<Array>} An array of submission objects.
 */
export const fetchAllSubmissions = async () => {
  try {
    const submissionsRef = collection(db, "submissions"); // Adjust if your collection name differs
    const submissionsQuery = query(
      submissionsRef,
      orderBy("createdAt", "desc")
    );
    const snapshot = await getDocs(submissionsQuery);

    // Map documents to submission objects
    const submissions = snapshot.docs.map((doc) => ({
      id: doc.id,
      ...doc.data(),
    }));

    return submissions;
  } catch (error) {
    console.error("Error fetching submissions:", error);
    throw error;
  }
};
